import { HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import fileSaver from 'file-saver';
import { JsonHttpService } from '../../shared/http/json-http.service';

/**
 * This service is a generic service used to download files from the DownloadController
 */
@Injectable({
    providedIn: 'root'
})
export class DownloaderService {
    private static readonly fallbackFileName = 'Unknown.txt';

    private readonly httpService = inject(JsonHttpService);

    async downloadFile(token: string): Promise<void> {
        const options = {
            // The endpoint returns the file payload and the original filename in response headers.
            observe: 'response',
            responseType: 'blob',
            params: new HttpParams().set('token', token)
        };

        const downloadResponse$ = this.httpService.getRaw('api/download/download-file', options) as Observable<HttpResponse<Blob>>;
        const response = await firstValueFrom(downloadResponse$);

        const filename = response.headers.get('x-filename')
            ?? this.getFileNameFromHttpResponse(response)
            ?? DownloaderService.fallbackFileName;

        if (response.body === null) {
            throw new Error('Download response did not include a file body.');
        }

        fileSaver.saveAs(response.body, filename);
    }

    private getFileNameFromHttpResponse(httpResponse: HttpResponse<Blob>): string {
        const contentDispositionHeader = httpResponse.headers.get('Content-Disposition');
        if (!contentDispositionHeader) {
            return null;
        }

        // Prefer filename* (RFC 5987) — supports charset-encoded names, e.g. UTF-8''some%20file.txt
        const filenameStarMatch = contentDispositionHeader.match(/filename\*\s*=\s*([^;]+)/i);
        if (filenameStarMatch) {
            const rfc5987Match = filenameStarMatch[1].trim().match(/^[^']*'[^']*'(.+)$/);
            if (rfc5987Match) {
                try {
                    return decodeURIComponent(rfc5987Match[1]);
                } catch {
                    // fall through to plain filename
                }
            }
        }

        // Fall back to filename="..." or filename=...
        const filenameMatch = contentDispositionHeader.match(/filename(?!\*)\s*=\s*"?([^";]+)"?/i);
        return filenameMatch ? filenameMatch[1].trim() : null;
    }
}
